import { useEffect, useRef, useState } from "preact/hooks";
import type { Ctx } from "../app";
import { sb } from "../lib/supabase";
import { go } from "../lib/ui";

interface Hit { href: string; label: string; sub: string }

/** Global search across BCBAs (name, email, NPI, CAQH ID) and payers. Press "/" to focus. */
export function SearchBox({ ctx }: { ctx: Ctx }) {
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<Hit[]>([]);
  const [sel, setSel] = useState(0);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const k = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      if (e.key === "/" && !["INPUT", "TEXTAREA", "SELECT"].includes(t.tagName)) { e.preventDefault(); input.current?.focus(); }
    };
    addEventListener("keydown", k);
    return () => removeEventListener("keydown", k);
  }, []);

  useEffect(() => {
    const term = q.trim().replace(/[%,()]/g, "");
    if (term.length < 2) { setHits([]); return; }
    const t = setTimeout(async () => {
      const like = `%${term}%`;
      const [p, y] = await Promise.all([
        sb().from("providers").select("id, first_name, last_name, email, npi, status").eq("org_id", ctx.orgId)
          .or(`first_name.ilike.${like},last_name.ilike.${like},email.ilike.${like},npi.ilike.${like},caqh_id.ilike.${like}`).limit(8),
        sb().from("payers").select("slug, name, states").ilike("name", like).limit(5),
      ]);
      setHits([
        ...(p.data ?? []).map((x) => ({ href: `providers/${x.id}`, label: `${x.first_name} ${x.last_name}`, sub: `BCBA · ${x.npi ?? x.email}` })),
        ...(y.data ?? []).map((x) => ({ href: `payers/${x.slug}`, label: x.name, sub: `Payer · ${(x.states as string[]).join(", ")}` })),
      ]);
      setSel(0);
    }, 200);
    return () => clearTimeout(t);
  }, [q, ctx.orgId]);

  const open = (h: Hit) => { go(h.href); setQ(""); setHits([]); input.current?.blur(); };
  return (
    <div class="search-box">
      <input ref={input} type="search" placeholder="Search ( / )" aria-label="Search BCBAs and payers" value={q}
        onInput={(e) => setQ(e.currentTarget.value)}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") { e.preventDefault(); setSel((s) => Math.min(s + 1, hits.length - 1)); }
          if (e.key === "ArrowUp") { e.preventDefault(); setSel((s) => Math.max(s - 1, 0)); }
          if (e.key === "Enter" && hits[sel]) open(hits[sel]!);
          if (e.key === "Escape") { setQ(""); setHits([]); }
        }} />
      {hits.length > 0 && (
        <div class="search-results" role="listbox">
          {hits.map((h, i) => (
            <a href={`#/${h.href}`} class={i === sel ? "sel" : ""} onClick={(e) => { e.preventDefault(); open(h); }}>
              <strong>{h.label}</strong><div class="muted small">{h.sub}</div>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
